import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type {
  Project,
  PanelStats,
  CastingStats,
  HardwareStats,
  IFCStats,
  TrailerLoadsStats,
  ServiceStore,
} from "types/ProjectDashboardTypes";
import type { PurchaseOrder } from "types/PurchaseOrderTypes";

interface ExtendedRootStore extends RootStore {
  jobStore?: ServiceStore<Project>;
  purchaseOrdersStore?: ServiceStore<PurchaseOrder>;
  accountsStore?: ServiceStore<unknown>;
  jobStatsPanelCountStore?: ServiceStore<PanelStats>;
  jobStatsCastingDatesStore?: ServiceStore<CastingStats>;
  jobStatsHardwareCountStore?: ServiceStore<HardwareStats>;
  jobStatsIfcStatusStore?: ServiceStore<IFCStats>;
  jobStatsTrailerLoadsStore?: ServiceStore<TrailerLoadsStats>;
}

class ProjectDashboardStore {
  loading = false;
  error: string | null = null;

  rootStore: ExtendedRootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore as ExtendedRootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  get selectedJobNumber(): string | null {
    return (this.rootStore.routerStore?.params?.jobNumber as string) || null;
  }

  get project(): Project | null {
    if (!this.selectedJobNumber) return null;

    const jobStore = this.rootStore.jobStore;
    if (!jobStore) return null;
    // Finding job by selected job number

    return (jobStore.entities || []).find((job) => job.jobNumber === Number(this.selectedJobNumber)) || null;
  }

  get purchaseOrders(): PurchaseOrder[] {
    if (!this.project?.accountId) return [];

    const purchaseOrdersStore = this.rootStore.purchaseOrdersStore;
    if (!purchaseOrdersStore) return [];

    // Filter by accountId like in ProjectDetails store since that's the relationship that works.
    // Guard `.entities` with `|| []` to match every sibling getter below: `rootStore.purchaseOrdersStore`
    // can resolve to a non-ServiceStore (name collision with the accounting ViewStore), whose
    // `.entities` is undefined and would otherwise throw here.
    return (purchaseOrdersStore.entities || []).filter(
      (po: PurchaseOrder) => po.accountId === this.project?.accountId
    );
  }

  get account() {
    if (!this.project?.accountId) return null;

    // Service is registered as name:"accounts" => rootStore.accountsStore (plural). The previous
    // singular `accountStore` was always undefined, so this getter never returned an account.
    const accountsStore = this.rootStore.accountsStore;
    if (!accountsStore) return null;

    // Use `.entities.find()` per CLAUDE.md (getById is forbidden); accounts use the `accountId` idField.
    return (
      (accountsStore.entities || []).find(
        (a) => (a as { accountId?: number }).accountId === this.project?.accountId
      ) || null
    );
  }

  get panelStats(): PanelStats | null {
    if (!this.project?.jobNumber) return null;

    const panelStatsStore = this.rootStore.jobStatsPanelCountStore;
    if (!panelStatsStore) return this.generateMockPanelStats();

    const stats = panelStatsStore.entities || [];
    const foundStats = stats.find((s) => s.jobNumber === this.project?.jobNumber);
    return foundStats || this.generateMockPanelStats();
  }

  get castingStats(): CastingStats | null {
    if (!this.project?.jobNumber) return null;

    const castingStatsStore = this.rootStore.jobStatsCastingDatesStore;
    if (!castingStatsStore) return this.generateMockCastingStats();

    const stats = castingStatsStore.entities || [];
    const foundStats = stats.find(
      (s) => s.jobNumber === this.project?.jobNumber
    );

    return foundStats || this.generateMockCastingStats();
  }

  get hardwareStats(): HardwareStats | null {
    if (!this.project?.jobNumber) return null;

    const hardwareStatsStore = this.rootStore.jobStatsHardwareCountStore;
    if (!hardwareStatsStore) return this.generateMockHardwareStats();

    const stats = hardwareStatsStore.entities || [];
    const foundStats = stats.find(
      (s) => s.jobNumber === this.project?.jobNumber
    );

    return foundStats || this.generateMockHardwareStats();
  }

  get ifcStats(): IFCStats | null {
    if (!this.project?.jobNumber) return null;

    const ifcStatsStore = this.rootStore.jobStatsIfcStatusStore;
    if (!ifcStatsStore) return this.generateMockIFCStats();

    const stats = ifcStatsStore.entities || [];
    const foundStats = stats.find(
      (s) => s.jobNumber === this.project?.jobNumber
    );

    return foundStats || this.generateMockIFCStats();
  }

  get trailerLoadsStats(): TrailerLoadsStats | null {
    if (!this.project?.jobNumber) return null;

    const trailerStatsStore = this.rootStore.jobStatsTrailerLoadsStore;
    if (!trailerStatsStore) return this.generateMockTrailerStats();

    const stats = trailerStatsStore.entities || [];
    const foundStats = stats.find(
      (s) => s.jobNumber === this.project?.jobNumber
    );

    return foundStats || this.generateMockTrailerStats();
  }

  get budgetVsActualData() {
    if (!this.project) return null;

    const budget =
      this.project.budget ||
      this.generateBudgetFromJobNumber(this.project.jobNumber);
    const actual = this.project.actualCost || budget * 0.65; // Mock actual cost at 65% of budget

    return {
      budget,
      actual,
      variance: budget - actual,
      percentUsed: (actual / budget) * 100,
    };
  }

  get progressData() {
    if (!this.project) return null;

    // Use actual progress if available, otherwise generate mock progress
    let progress = this.project.progress || 0;

    // If no progress, generate realistic mock data based on project timeline
    if (progress === 0 && this.project.createdAt) {
      const createdDate = new Date(this.project.createdAt);
      const now = new Date();
      const daysSinceCreation = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

      // Assume project takes ~180 days, calculate progress based on time elapsed
      // Add some randomness based on job number for variety
      const seed = (this.project.jobNumber || 1) * 7;
      const randomAdjustment = (seed % 10) - 5; // -5 to +5 adjustment
      progress = Math.min(95, Math.max(5, Math.floor((daysSinceCreation / 180) * 100) + randomAdjustment));

      // If project is very new, at least show some initial progress
      if (daysSinceCreation < 30 && progress < 15) {
        progress = 15 + (seed % 5);
      }

      // For demo purposes, set a realistic progress value
      progress = 68; // Project is in Production phase
    }

    // Calculate stage completion percentages based on overall progress
    const stages = [
      {
        name: "Planning",
        value: progress >= 15 ? 100 : Math.round((progress / 15) * 100)
      },
      {
        name: "Design",
        value: progress >= 35 ? 100 : (progress >= 15 ? Math.round(((progress - 15) / 20) * 100) : 0)
      },
      {
        name: "Production",
        value: progress >= 75 ? 100 : (progress >= 35 ? Math.round(((progress - 35) / 40) * 100) : 0)
      },
      {
        name: "Installation",
        value: progress >= 95 ? 100 : (progress >= 75 ? Math.round(((progress - 75) / 20) * 100) : 0)
      },
      {
        name: "Final QC",
        value: progress >= 100 ? 100 : (progress >= 95 ? Math.round(((progress - 95) / 5) * 100) : 0)
      },
    ];

    // Determine current stage based on progress
    let currentStage = "Planning";
    if (progress >= 95) currentStage = "Final QC";
    else if (progress >= 75) currentStage = "Installation";
    else if (progress >= 35) currentStage = "Production";
    else if (progress >= 15) currentStage = "Design";

    return {
      overall: progress,
      stages,
      currentStage: this.project.stage || currentStage,
    };
  }

  generateBudgetFromJobNumber(jobNumber: number): number {
    const seed = jobNumber * 12345 + 67890;
    const random = Math.abs(Math.sin(seed)) * 10000;
    const minBudget = 50000;
    const maxBudget = 2000000;
    const budget = minBudget + (random % (maxBudget - minBudget));
    return Math.round(budget / 5000) * 5000;
  }

  generateStageFromJobNumber(jobNumber: number): string {
    const stages = [
      "Planning",
      "Design",
      "Permitting",
      "Pre-Construction",
      "Production",
      "Installation",
      "Quality Control",
      "Final Inspection",
      "Completed",
    ];
    const seed = jobNumber * 54321 + 13579;
    const random = Math.abs(Math.sin(seed));
    const index = Math.floor(random * stages.length);
    return stages[index];
  }

  generateMockPanelStats(): PanelStats | null {
    if (!this.project) return null;
    const jobNumber = this.project.jobNumber;
    const seed = jobNumber * 13;
    const totalPanels = 250 + (seed % 750);
    const panelCount = Math.floor(totalPanels * 0.7);
    const assemblyCount = Math.floor(totalPanels * 0.3);

    return {
      jobNumber,
      panelCount: {
        total: totalPanels,
        panel: panelCount,
        assembly: assemblyCount,
      },
      uniqueMarkCount: Math.floor(totalPanels * 0.6),
    };
  }

  generateMockCastingStats(): CastingStats | null {
    if (!this.project) return null;
    const jobNumber = this.project.jobNumber;
    const totalPanels = 500 + (jobNumber % 1000);
    const castPercentage = 0.35 + (jobNumber % 30) / 100;

    return {
      jobNumber,
      startCasting: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      endCasting: new Date(Date.now() + 120 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      moldCount: 20 + (jobNumber % 30),
      scheduled: Math.floor(totalPanels * 0.8),
      unscheduled: Math.floor(totalPanels * 0.2),
      alreadyCastPanels: Math.floor(totalPanels * castPercentage),
      remainingPanelsToCast: Math.floor(totalPanels * (1 - castPercentage)),
      panelsOnHold: Math.floor(totalPanels * 0.05),
      totalRecast: Math.floor(totalPanels * 0.02),
      castSqFt: Math.floor(totalPanels * castPercentage * 120),
      remainingSqFt: Math.floor(totalPanels * (1 - castPercentage) * 120),
      unscheduledSqFt: Math.floor(totalPanels * 0.2 * 120),
      castCubicYard: Math.floor(totalPanels * castPercentage * 2.5),
      remainingCubicYard: Math.floor(totalPanels * (1 - castPercentage) * 2.5),
      unscheduledCubicYards: Math.floor(totalPanels * 0.2 * 2.5),
      totalVeneerSqFt: Math.floor(totalPanels * 0.3 * 100),
      castVeneerCount: Math.floor(totalPanels * 0.3 * castPercentage),
      remainingVeneerCount: Math.floor(
        totalPanels * 0.3 * (1 - castPercentage)
      ),
      castLooseBarCount: Math.floor(totalPanels * 2 * castPercentage),
      remainingLooseBarCount: Math.floor(
        totalPanels * 2 * (1 - castPercentage)
      ),
      castWeldInches: Math.floor(totalPanels * 48 * castPercentage),
      remainingWeldInches: Math.floor(totalPanels * 48 * (1 - castPercentage)),
    };
  }

  generateMockHardwareStats(): HardwareStats | null {
    if (!this.project) return null;
    const jobNumber = this.project.jobNumber;
    const base = 100 + (jobNumber % 500);
    // Deterministic pseudo-random offsets seeded off jobNumber (mirrors generateBudgetFromJobNumber)
    // so this MobX computed stays stable across renders instead of flickering on every recompute.
    const seeded = (salt: number) => Math.abs(Math.sin(jobNumber * salt));

    return {
      jobNumber,
      platesCount: base + Math.floor(seeded(101) * 200),
      anglesCount: Math.floor(base * 0.8) + Math.floor(seeded(211) * 150),
      embedsCount: Math.floor(base * 0.6) + Math.floor(seeded(307) * 100),
      boltsCount: Math.floor(base * 1.5) + Math.floor(seeded(401) * 300),
      otherCount: Math.floor(base * 0.3) + Math.floor(seeded(509) * 50),
      totalHardware: base * 4,
    };
  }

  generateMockIFCStats(): IFCStats | null {
    if (!this.project) return null;
    const jobNumber = this.project.jobNumber;
    const totalItems = 200 + (jobNumber % 300);

    return {
      jobNumber,
      approvedCount: Math.floor(totalItems * 0.65),
      pendingCount: Math.floor(totalItems * 0.15),
      inReviewCount: Math.floor(totalItems * 0.12),
      rejectedCount: Math.floor(totalItems * 0.05),
      onHoldCount: Math.floor(totalItems * 0.03),
      totalIFCItems: totalItems,
    };
  }

  generateMockTrailerStats(): TrailerLoadsStats | null {
    if (!this.project) return null;
    const jobNumber = this.project.jobNumber;
    const totalLoads = 30 + (jobNumber % 50);

    return {
      jobNumber,
      totalLoads,
      deliveredLoads: Math.floor(totalLoads * 0.4),
      inTransitLoads: Math.floor(totalLoads * 0.1),
      scheduledLoads: Math.floor(totalLoads * 0.5),
    };
  }

  async refreshProjectData(jobNumber: number) {
    runInAction(() => {
      this.setLoading(true);
      this.setError(null);
    });

    try {
      // Load project data
      const jobStore = this.rootStore.jobStore;
      if (!jobStore) {
        throw new Error("Job store not available");
      }

      await jobStore.find({ query: { jobNumber: jobNumber } });

      const project = (jobStore.entities || []).find((p) => p.jobNumber === Number(jobNumber));
      if (project) {
        // Load related data in parallel
        const promises: Promise<unknown>[] = [];

        // Load purchase orders
        const purchaseOrdersStore = this.rootStore.purchaseOrdersStore;
        if (purchaseOrdersStore) {
          promises.push(
            purchaseOrdersStore.find({
              query: {
                jobId: project.id,
                $limit: 50,
                $sort: { createdAt: -1 },
              },
            })
          );
        }

        // Load job stats if job number exists
        if (project.jobNumber) {
          const panelStatsStore = this.rootStore.jobStatsPanelCountStore;
          if (panelStatsStore) {
            promises.push(
              panelStatsStore.find({
                query: { jobNumber: project.jobNumber },
              })
            );
          }

          const castingStatsStore = this.rootStore.jobStatsCastingDatesStore;
          if (castingStatsStore) {
            promises.push(
              castingStatsStore.find({
                query: { jobNumber: project.jobNumber },
              })
            );
          }

          const hardwareStatsStore = this.rootStore.jobStatsHardwareCountStore;
          if (hardwareStatsStore) {
            promises.push(
              hardwareStatsStore.find({
                query: { jobNumber: project.jobNumber },
              })
            );
          }

          const ifcStatsStore = this.rootStore.jobStatsIfcStatusStore;
          if (ifcStatsStore) {
            promises.push(
              ifcStatsStore.find({
                query: { jobNumber: project.jobNumber },
              })
            );
          }

          const trailerStatsStore = this.rootStore.jobStatsTrailerLoadsStore;
          if (trailerStatsStore) {
            promises.push(
              trailerStatsStore.find({
                query: { jobNumber: project.jobNumber },
              })
            );
          }
        }

        // Load account data
        if (project.accountId) {
          const accountsStore = this.rootStore.accountsStore;
          if (accountsStore) {
            promises.push(accountsStore.get(project.accountId));
          }
        }

        await Promise.all(promises);
      }
    } catch (error) {
      console.error("Error refreshing project data:", error);
      runInAction(() => {
        this.setError(
          (error as Error)?.message || "Failed to load project data"
        );
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }
}

export default ProjectDashboardStore;
